import React, { useState, useEffect } from "react";
import KruozAPI from "../../KruozAPI";
import moment from "moment";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "react-hot-toast";

const SectionCard = ({ title, children }) => (
  <div className="bg-white shadow-md rounded-lg p-6 mb-6">
    <h3 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-200">
      {title}
    </h3>
    {children}
  </div>
);

const OrderSummaryCard = ({ orderId, onNotFound }) => {
  const [order, setOrder] = useState(null);
  const [orderLines, setOrderLines] = useState([]);
  const [originalOrderLines, setOriginalOrderLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // can be string or { status, message }
  const [paid, setPaid] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentReference, setPaymentReference] = useState("");

  useEffect(() => {
    if (!orderId) return;

    const fetchOrder = async () => {
      setLoading(true);
      try {
        const response = await KruozAPI.get(`/Order/ById?id=${orderId}`);
        setOrder(response.data);
        fetchOrderLines(response.data.orderNumber);
        fetchPayments(response.data.orderNumber);
      } catch (err) {
        const status = err?.response?.status || err?.status;
        if (status === 404) {
          setError({ status: 404, message: "Order not found" });
          try { onNotFound && onNotFound(); } catch (_) {}
        } else {
          const msg = err?.message || "Unknown error";
          setError({ status: status || 0, message: `Failed to fetch order details - ${msg}` });
        }
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const fetchPayments = async (orderNumber) => {
    try {
      const response = await KruozAPI.get(
        `/Order/OrderPayments?orderNumber=${orderNumber}`
      );
      const totalPaid = response.data.reduce(
        (sum, payment) => sum + payment.amount,
        0
      );
      setPaid(totalPaid);
    } catch (err) {
      console.error("Failed to fetch payments:", err);
    }
  };

  const fetchOrderLines = async (orderNumber) => {
    try {
      const response = await KruozAPI.get(
        `/Order/OrderLines?orderId=${orderNumber}`
      );
      setOriginalOrderLines(response.data);
      setOrderLines(response.data.filter((x) => x.assetId == null));
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch order lines");
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setLoading(true);
    try {
      const response = await KruozAPI.get(
        `/Order/DownloadInvoice?orderNumber=${order.orderNumber}`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${order.orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setLoading(false);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(
        "Downloading invoice failed. Please contact the service provider"
      );
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const handleSavePayment = (paymentReference, by) => {
    setLoading(true);
    var data = {
      orderId: order.id,
      orderNumber: order.orderNumber,
      paymentMethod: "Paypal",
      amount: paidAmount,
      paymentDate: new Date().toISOString(),
      paymentStatus: "Paid",
      paymentReference: paymentReference,
      paymentGatewayReference: by,
      companyId: order.companyId,
    };
    KruozAPI.post("/Order/OrderPayment", data)
      .then((response) => {
        setLoading(false);
        fetchPayments(order.orderNumber);
      })
      .catch((error) => {
        setLoading(false);
        toast.error("Payment update failed - " + error);
        console.error("Payment update failed:", error);
      });
  };

  const createOrder = (data, actions) => {
    const timestampId = new Date().getTime().toString();
    var balance = calculateBalance();
    setPaidAmount(balance);
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: balance,
          },
          invoice_id: order.orderNumber + "_" + timestampId,
        },
      ],
    });
  };

  const onApprove = (data, actions) => {
    return actions.order.capture().then(function (details) {
      setPaymentReference(data.orderID);
      toast.success(
        "Payment processed successfully, by -" + details.payer.name.given_name
      );
      handleSavePayment(
        data ? data.orderID : "NoOrderNumberFound",
        details?.payer?.name?.given_name || ""
      );
    });
  };

  const onError = (err) => {
    let errMsg = "Error occurred while processing the payment.";
    if (err.message) {
      try {
        const errorParts = err.message.split("\n\n");
        const errorObj = JSON.parse(errorParts[1]);
        errMsg = errorObj.message;
        if (errorObj.details?.[0]) {
          errMsg =
            errorObj.details[0].issue +
            " - " +
            errorObj.details[0].description +
            errMsg;
        }
      } catch (error) {
        console.error("Error parsing error response:", error);
      }
    }
    toast.error(errMsg);
    document
      .querySelector(".paypal-buttons-frame")
      ?.style.setProperty("display", "none");
  };

  const calculateBalance = () => {
    let subTotal = 0;
    let gstTotal = 0;
    let refund = 0;
    let refundGst = 0;
    let discounts = 0;
    let discountGst = 0;

    // Calculate from assets
    if (order?.selectedAssets) {
      order.selectedAssets.forEach((asset) => {
        subTotal += asset.cost;
        if (asset.gst) {
          gstTotal += asset.gst;
        }
      });
    }

    // Calculate from order lines
    if (orderLines) {
      orderLines.forEach((line) => {
        if (line.unitType === "refund") {
          refund += line.total;
          refundGst += line.gst || 0;
        } else if (line.unitType === "discount") {
          discounts += line.total;
          discountGst += line.gst || 0;
        } else if (!line.assetId) {
          subTotal += line.total;
        }
        if (line.gst) {
          gstTotal += line.gst;
        }
      });
    }

    return parseFloat(
      (
        subTotal +
        gstTotal +
        discounts +
        discountGst +
        refund +
        refundGst -
        paid
      ).toFixed(2)
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    const status = typeof error === "object" ? error.status : undefined;
    const message = typeof error === "object" ? error.message : error;
    if (status === 404) {
      return (
        <SectionCard title="Order not found">
          <div className="flex flex-col items-center text-center py-10">
            <div className="text-6xl mb-4">🧾</div>
            <p className="text-gray-700 mb-6">
              We couldn't find an order with ID <span className="font-semibold">{orderId}</span>.
              It may have been removed, completed, or the link is incorrect.
            </p>
            <div className="flex gap-3">
              <button onClick={() => window.location.reload()} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md">Retry</button>
            </div>
          </div>
        </SectionCard>
      );
    }
    return (
      <div className="p-6 bg-red-50 border border-red-200 text-red-700 rounded-md">
        {message}
      </div>
    );
  }

  if (!order) return null;

  let subTotal = 0;
  let gstTotal = 0;
  let refund = 0;
  let refundGst = 0;
  let discounts = 0;
  let discountGst = 0;

  // Calculate totals from selected assets
  if (
    order &&
    originalOrderLines &&
    order.selectedAssets &&
    order.selectedAssets.length > 0 &&
    originalOrderLines.length > 0
  ) {
    for (let i = 0; i < order.selectedAssets.length; i++) {
      subTotal += order.selectedAssets[i].cost;
      var matchedOrderLine = originalOrderLines.find(
        (x) => x.assetId == order.selectedAssets[i].id
      );

      if (matchedOrderLine) {
        if (matchedOrderLine.gst) {
          order.selectedAssets[i].gst = matchedOrderLine.gst;
          gstTotal += matchedOrderLine.gst;
        }
      }
    }
  }

  // Add totals from order lines
  if (orderLines && orderLines.length > 0) {
    for (let i = 0; i < orderLines.length; i++) {
      if (orderLines[i].unitType == "refund") {
        refund += orderLines[i].total;
        refundGst += orderLines[i].gst;
      } else if (orderLines[i].unitType == "discount") {
        discounts += orderLines[i].total;
        discountGst += orderLines[i].gst;
      } else if (orderLines[i].assetId == null) {
        subTotal += orderLines[i].total;
      }
      if (orderLines[i].gst) {
        gstTotal += orderLines[i].gst;
      }
    }
  }

  const balance = calculateBalance();
  const paypalOptions = {
    clientId: order.paypalClientId,
    currency: "AUD",
    intent: "capture",
    "disable-funding": "card",
  };
  var orderTitle = "Order Summary - "+order.orderNumber;
  return (
    <SectionCard title={orderTitle}>
      <div className="space-y-4">
        {/* Payment and Invoice Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 border-b pb-8">
          {balance > 0 && order.paypalClientId && (
            <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h4 className="text-gray-600 text-sm mb-1">Outstanding Balance</h4>
                  <div className="text-2xl font-bold text-gray-900">${formatNumber(balance)}</div>
                </div>
                <div className="flex gap-3">
                  <svg className="w-14 h-9" viewBox="0 0 56 36">
                    <rect width="56" height="36" rx="4" fill="#1A1F71"/>
                    <text x="28" y="22" fontSize="16" fill="white" textAnchor="middle" fontWeight="bold">VISA</text>
                  </svg>
                  <svg className="w-14 h-9" viewBox="0 0 56 36">
                    <rect width="56" height="36" rx="4" fill="#EB001B"/>
                    <circle cx="21" cy="18" r="11" fill="#FF5F00"/>
                    <circle cx="35" cy="18" r="11" fill="#F79E1B"/>
                  </svg>
                </div>
              </div>
              <div className="mb-1">
                <div className="text-sm text-gray-600">Secure Payment via PayPal</div>
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtons
                    style={{ layout: "horizontal" }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={onError}
                  />
                </PayPalScriptProvider>
              </div>
            </div>
          )}
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 shadow-sm flex flex-col justify-center">
            <div className="mb-1">
              {/* <h4 className="text-gray-600 text-sm mb-1">Invoice Management</h4>
              <p className="text-gray-700 mb-3">Download your invoice for record keeping and tax purposes</p> */}
              <button
              onClick={handleDownloadInvoice}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white flex justify-center items-center rounded-lg transition-colors"
            >
              <span className="font-medium">Download Invoice</span>
            </button>
            </div>
            
          </div>
        </div>
        {/* Assets */}
        {order.selectedAssets.map((asset, index) => (
          <div key={index} className="border-b pb-4 last:border-b-0">
            <div className="flex justify-between mb-2">
              <div>
                <h4 className="font-medium text-gray-800">
                  {asset.code} - {asset.name}
                </h4>
                <p className="text-sm text-gray-600">
                  x{asset.units}{" "}
                  {asset.priceUnit === "Day" ? "Day(s)" : "Hours"}
                </p>
                <p className="text-sm text-gray-600">
                  {moment(asset.startDate).format("LL")}{" "}
                  {moment(asset.startDate).format("LT")} -
                  {moment(asset.endDate).format("LL")}{" "}
                  {moment(asset.endDate).format("LT")}
                </p>
              </div>
              <div className="text-right">
                <div className="font-medium">${formatNumber(asset.cost)}</div>
                {asset.gst > 0 && (
                  <div className="text-sm text-gray-600">
                    (${formatNumber(asset.gst)} GST)
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Order Lines */}
        {orderLines
          .filter(line => line.category?.toLowerCase() !== 'cancelled' && line.category?.toLowerCase() !== 'cancellation')
          .map((line, index) => (
          <div key={index} className="border-b pb-4 last:border-b-0">
            <div className="flex justify-between mb-2">
              <div>
                <h4 className="font-medium text-gray-800">{line.text}</h4>
                {line.category && (
                  <p className="text-sm text-gray-600">x {line.category}</p>
                )}
              </div>
              <div className="text-right">
                <div className="font-medium">${formatNumber(line.total)}</div>
                {line.gst !== 0 && (
                  <div className="text-sm text-gray-600">
                    (${formatNumber(line.gst)} GST)
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Totals */}
        <div className="pt-4 space-y-2">
          <div className="flex justify-between">
            <span className="font-medium">Subtotal</span>
            <span>${formatNumber(subTotal)}</span>
          </div>
          {gstTotal !== 0 && (
            <div className="flex justify-between text-gray-600">
              <span>GST</span>
              <span>${formatNumber(gstTotal)}</span>
            </div>
          )}
          {discounts !== 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Discount</span>
              <span>${formatNumber(discounts)}</span>
            </div>
          )}
          {discountGst !== 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Discount GST</span>
              <span>${formatNumber(discountGst)}</span>
            </div>
          )}
          {refund !== 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Refund</span>
              <span>${formatNumber(refund)}</span>
            </div>
          )}
          {refundGst !== 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Refund GST</span>
              <span>${formatNumber(refundGst)}</span>
            </div>
          )}
          <div className="flex justify-between text-gray-600">
            <span>Total Paid</span>
            <span>${formatNumber(paid)}</span>
          </div>
          <div className="flex justify-between font-bold text-lg pt-2 border-t">
            <span>Balance</span>
            <span>
              $
              {formatNumber(
                subTotal +
                  gstTotal +
                  discounts +
                  discountGst +
                  refund +
                  refundGst -
                  paid
              )}
            </span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default OrderSummaryCard;
